import { useNavigate } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import { usePageLayout } from '@app/router/index'
import { ROUTES } from '@app/router/routes'
import { useLanguage } from '@shared/contexts/LanguageContext'
import { useEffect } from 'react'

/**
 * 護博會子頁面（講座／技能交換市集／挑戰賽）共用架構
 * banner + 標題文字區塊 + 內容區塊，樣式與護博會首頁一致
 */
export default function GuardianExpoSubPageLayout({ bannerSrc, bannerAlt, subtitle, content, children }) {
  const { setLayoutOptions } = usePageLayout()
  const { t } = useLanguage()
  const navigate = useNavigate()

  useEffect(() => {
    setLayoutOptions({ noPaddingBottom: true })
    return () => setLayoutOptions({})
  }, [setLayoutOptions])

  return (
    <div style={{ backgroundColor: '#FEFEFE' }}>

      {/* ── 返回護博會首頁（懸浮，跟隨捲動固定在畫面上） ── */}
      <div className="fixed top-[80px] md:top-[96px] left-4 md:left-8 z-30">
        <button
          onClick={() => navigate(ROUTES.GUARDIAN_EXPO)}
          className="flex justify-center items-center w-10 h-10 rounded-full bg-white/60 shadow-[0_2px_8px_rgba(0,0,0,0.1)] backdrop-blur text-[var(--color-primary)] hover:bg-white transition-colors cursor-pointer"
          aria-label={t('guardianExpo.back')}
        >
          <ChevronLeft size={24} className="mr-0.5" />
        </button>
      </div>

      {/* ── Banner ── */}
      <div className="relative w-full">
        <img
          src={bannerSrc}
          alt={bannerAlt}
          className="w-full h-auto block"
        />
      </div>

      {/* ── Banner 文字（水平置中，同護博會首頁樣式） ── */}
      {(subtitle || content) && (
        <div className="relative w-full max-w-[430px] md:w-4/5 md:max-w-none mx-auto" style={{ zIndex: 1 }}>
          <section className="px-6 pt-10 pb-10 text-center flex flex-col items-center">
            {subtitle && (
              <h2
                className="font-['Noto_Serif_TC']"
                style={{ color: '#171717', fontSize: 24, fontWeight: 500, lineHeight: '36px' }}
              >
                {subtitle}
              </h2>
            )}
            {content && (
              <p
                className="font-['Noto_Sans_TC'] whitespace-pre-line"
                style={{ color: '#6E625B', fontSize: 20, fontWeight: 400, lineHeight: '32px', letterSpacing: '0.38px', marginTop: 12 }}
              >
                {content}
              </p>
            )}
          </section>
        </div>
      )}

      {/* ── 內容區塊（各子頁面自訂） ── */}
      {children}

    </div>
  )
}

/**
 * 內容區塊樣式（紅色色塊＋左側棕線標題，同中台心傳／護博會首頁）
 */
export function GuardianExpoSection({ titleBlack, titleRed, children, first = false, last = false }) {
  return (
    <div className="w-full max-w-[430px] md:w-4/5 md:max-w-none mx-auto">
      <section className={`px-6 ${last ? 'pb-10' : ''}`} style={first ? { paddingTop: 20 } : undefined}>
        {!first && <div className="h-10" style={{ borderLeft: '1px solid #6E625B', paddingLeft: 20 }} />}

        <div style={{ borderLeft: '6px solid #BC313D', paddingLeft: 20 }}>
          <h2 style={{ fontFamily: 'Noto Serif TC', fontSize: 30, fontWeight: 700, lineHeight: '33px' }}>
            <span style={{ color: '#171717' }}>{titleBlack}</span><br />
            <span style={{ color: '#BC313D' }}>{titleRed}</span>
          </h2>
        </div>

        <div style={{ borderLeft: '1px solid #6E625B', paddingLeft: 26, paddingTop: 24, paddingBottom: 16 }}>
          {children}
        </div>

        {!last && <div className="h-6" style={{ borderLeft: '1px solid #6E625B', paddingLeft: 20 }} />}
      </section>
    </div>
  )
}
